import { useCallback, useEffect, useState } from "react";
import { Globe2, KeyRound, Mail, PlugZap, Server, ShieldCheck, Trash2 } from "lucide-react";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  getOpenAIProviderSettings,
  getSmtpProviderSettings,
  removeOpenAIProviderKey,
  removeSmtpProviderSettings,
  saveOpenAIProviderKey,
  saveSmtpProviderSettings,
  testOpenAIProvider,
  testSmtpProvider,
} from "@/services/api";

const SMTP_DEFAULTS = {
  host: "",
  port: "587",
  username: "",
  password: "",
  security: "starttls",
  from_email: "",
  public_app_url: "https://aion.bidblitz.ae",
};

const SMTP_MISSING_LABELS = {
  AION_AUTH_SMTP_HOST: "SMTP Host",
  AION_AUTH_SMTP_PORT: "SMTP Port",
  AION_AUTH_SMTP_USERNAME: "SMTP Benutzername",
  AION_AUTH_SMTP_PASSWORD: "SMTP Passwort",
  AION_AUTH_SMTP_SECURITY: "SMTP Sicherheit",
  AION_AUTH_EMAIL_FROM: "Absender-E-Mail",
  AION_PUBLIC_APP_URL: "Öffentliche AION URL",
};

export const AdminAIProviderPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const [smtpStatus, setSmtpStatus] = useState(null);
  const [smtpForm, setSmtpForm] = useState(SMTP_DEFAULTS);
  const [smtpBusy, setSmtpBusy] = useState(false);
  const [smtpNotice, setSmtpNotice] = useState("");
  const [smtpError, setSmtpError] = useState("");

  const loadOpenAI = useCallback(async () => {
    try {
      setStatus(await getOpenAIProviderSettings(token));
    } catch (err) {
      setError(String(err?.message || "Provider-Status konnte nicht geladen werden."));
    }
  }, [token]);

  const loadSmtp = useCallback(async () => {
    try {
      const next = await getSmtpProviderSettings(token);
      setSmtpStatus(next);
      setSmtpForm((previous) => ({
        ...previous,
        ...(next?.config || {}),
        port: String(next?.config?.port || previous.port || "587"),
        password: "",
      }));
    } catch (err) {
      setSmtpError(String(err?.message || "SMTP-Status konnte nicht geladen werden."));
    }
  }, [token]);

  useEffect(() => {
    void loadOpenAI();
    void loadSmtp();
  }, [loadOpenAI, loadSmtp]);

  const save = async (event) => {
    event.preventDefault();
    if (apiKey.trim().length < 20) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await saveOpenAIProviderKey(apiKey.trim(), token);
      setApiKey("");
      setStatus((previous) => ({ ...previous, ...result }));
      setNotice("OpenAI wurde sicher mit AION verbunden.");
      await loadOpenAI();
    } catch (err) {
      setError(String(err?.message || "OpenAI-Key konnte nicht gespeichert werden."));
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await testOpenAIProvider(token);
      setNotice(result?.message || "OpenAI-Verbindung erfolgreich.");
      await loadOpenAI();
    } catch (err) {
      setError(String(err?.message || "OpenAI-Verbindungstest fehlgeschlagen."));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      await removeOpenAIProviderKey(token);
      setNotice("OpenAI-Key wurde aus AION entfernt.");
      await loadOpenAI();
    } catch (err) {
      setError(String(err?.message || "OpenAI-Key konnte nicht entfernt werden."));
    } finally { setBusy(false); }
  };

  const updateSmtpField = (field, value) => {
    setSmtpForm((previous) => ({ ...previous, [field]: value }));
  };

  const saveSmtp = async (event) => {
    event.preventDefault();
    setSmtpError(""); setSmtpNotice("");
    if (!smtpForm.host.trim() || !smtpForm.from_email.trim()) {
      setSmtpError("SMTP Host und Absender-E-Mail sind erforderlich.");
      return;
    }
    setSmtpBusy(true);
    try {
      const payload = {
        host: smtpForm.host.trim(),
        port: Number(smtpForm.port) || (smtpForm.security === "ssl" ? 465 : 587),
        username: smtpForm.username.trim(),
        security: smtpForm.security,
        from_email: smtpForm.from_email.trim(),
        public_app_url: smtpForm.public_app_url.trim() || "https://aion.bidblitz.ae",
        ...(smtpForm.password.trim() ? { password: smtpForm.password.trim() } : {}),
      };
      const result = await saveSmtpProviderSettings(payload, token);
      setSmtpStatus(result);
      setSmtpForm((previous) => ({ ...previous, password: "" }));
      setSmtpNotice("SMTP wurde sicher gespeichert. AION kann die Registrierung damit freischalten, sobald der Status bereit ist.");
      await loadSmtp();
    } catch (err) {
      setSmtpError(String(err?.message || "SMTP-Konfiguration konnte nicht gespeichert werden."));
    } finally { setSmtpBusy(false); }
  };

  const testSmtp = async () => {
    setSmtpBusy(true); setSmtpError(""); setSmtpNotice("");
    try {
      const result = await testSmtpProvider(token);
      setSmtpNotice(result?.message || "SMTP-Verbindung erfolgreich. Es wurde keine E-Mail versendet.");
      await loadSmtp();
    } catch (err) {
      setSmtpError(String(err?.message || "SMTP-Verbindungstest fehlgeschlagen."));
    } finally { setSmtpBusy(false); }
  };

  const removeSmtp = async () => {
    setSmtpBusy(true); setSmtpError(""); setSmtpNotice("");
    try {
      await removeSmtpProviderSettings(token);
      setSmtpForm(SMTP_DEFAULTS);
      setSmtpNotice("SMTP-Konfiguration wurde aus AION entfernt.");
      await loadSmtp();
    } catch (err) {
      setSmtpError(String(err?.message || "SMTP-Konfiguration konnte nicht entfernt werden."));
    } finally { setSmtpBusy(false); }
  };

  const smtpMissing = (smtpStatus?.delivery?.missing || []).map((item) => SMTP_MISSING_LABELS[item] || item);

  return (
    <div className="aion-view aion-view-stack" data-testid="admin-ai-provider-panel">
      <AionSectionCard
        title="OpenAI · AION Voice"
        eyebrow="AI & VOICE"
        subtitle="Der API-Key wird ausschließlich serverseitig verschlüsselt gespeichert und danach nicht mehr vollständig angezeigt."
        testId="admin-openai-provider-card"
      >
        <div className="aion-compact-status-grid">
          <div className="aion-compact-status-box"><span>OpenAI</span><strong>{status?.configured ? "VERBUNDEN" : "NICHT VERBUNDEN"}</strong></div>
          <div className="aion-compact-status-box"><span>Runtime</span><strong>{status?.runtime_loaded ? "AKTIV" : "AUS"}</strong></div>
          <div className="aion-compact-status-box"><span>Speicher</span><strong>{status?.stored_encrypted ? "VERSCHLÜSSELT" : "—"}</strong></div>
          <div className="aion-compact-status-box"><span>Key</span><strong>{status?.suffix ? `••••${status.suffix}` : "—"}</strong></div>
          <div className="aion-compact-status-box"><span>Realtime</span><strong>{status?.models?.realtime || "gpt-realtime-2.1"}</strong></div>
          <div className="aion-compact-status-box"><span>Voice bereit</span><strong>{status?.configuration?.ready_for_live_voice ? "JA" : "NOCH NICHT"}</strong></div>
        </div>

        <form onSubmit={save} className="aion-form-stack">
          <label>
            <span>Neuen OpenAI API-Key eintragen</span>
            <Input type="password" autoComplete="new-password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder="sk-proj-…" data-testid="admin-openai-key-input" />
          </label>
          <div className="aion-button-row">
            <Button type="submit" disabled={busy || apiKey.trim().length < 20}><KeyRound size={16} /> Sicher speichern</Button>
            <Button type="button" variant="outline" disabled={busy || !status?.configured} onClick={testConnection}><PlugZap size={16} /> Verbindung testen</Button>
            <Button type="button" variant="outline" disabled={busy || !status?.configured} onClick={remove}><Trash2 size={16} /> Key entfernen</Button>
          </div>
        </form>

        {notice ? <div className="aion-note"><ShieldCheck size={16} /> {notice}</div> : null}
        {error ? <div className="aion-error-box">{error}</div> : null}
        <div className="aion-note">Den Key niemals in Chat, GitHub oder Frontend-Code einfügen.</div>
      </AionSectionCard>

      <AionSectionCard
        title="E-Mail & Registrierung"
        eyebrow="SMTP"
        subtitle="AION nutzt SMTP ausschließlich für Verifizierung, Passwort-Reset und sichere Konto-Einrichtung. Das Passwort wird verschlüsselt gespeichert."
        testId="admin-smtp-provider-card"
      >
        <div className="aion-compact-status-grid">
          <div className="aion-compact-status-box"><span>SMTP</span><strong>{smtpStatus?.ready ? "BEREIT" : "NICHT BEREIT"}</strong></div>
          <div className="aion-compact-status-box"><span>Runtime</span><strong>{smtpStatus?.runtime_loaded ? "AKTIV" : "AUS"}</strong></div>
          <div className="aion-compact-status-box"><span>Passwort</span><strong>{smtpStatus?.password_stored_encrypted ? "VERSCHLÜSSELT" : smtpStatus?.password_configured ? "AKTIV" : "—"}</strong></div>
          <div className="aion-compact-status-box"><span>Sicherheit</span><strong>{String(smtpStatus?.delivery?.security || smtpForm.security).toUpperCase()}</strong></div>
          <div className="aion-compact-status-box"><span>Port</span><strong>{smtpStatus?.delivery?.port || smtpForm.port || "—"}</strong></div>
          <div className="aion-compact-status-box"><span>Registrierung</span><strong>{smtpStatus?.ready ? "FREIGESCHALTET" : "GESPERRT"}</strong></div>
        </div>

        <form onSubmit={saveSmtp} className="aion-form-stack" data-testid="admin-smtp-form">
          <label>
            <span>SMTP Host</span>
            <Input value={smtpForm.host} onChange={(event) => updateSmtpField("host", event.target.value)} placeholder="smtp.example.com" autoComplete="off" />
          </label>
          <label>
            <span>SMTP Port</span>
            <Input type="number" min="1" max="65535" value={smtpForm.port} onChange={(event) => updateSmtpField("port", event.target.value)} placeholder="587" />
          </label>
          <div className="aion-button-row">
            <Button type="button" variant={smtpForm.security === "starttls" ? "default" : "outline"} onClick={() => updateSmtpField("security", "starttls")}>STARTTLS</Button>
            <Button type="button" variant={smtpForm.security === "ssl" ? "default" : "outline"} onClick={() => updateSmtpField("security", "ssl")}>SSL</Button>
          </div>
          <label>
            <span>SMTP Benutzername</span>
            <Input value={smtpForm.username} onChange={(event) => updateSmtpField("username", event.target.value)} placeholder="user@example.com" autoComplete="username" />
          </label>
          <label>
            <span>SMTP Passwort / App-Passwort</span>
            <Input type="password" value={smtpForm.password} onChange={(event) => updateSmtpField("password", event.target.value)} placeholder={smtpStatus?.password_configured ? "Gespeichert – leer lassen zum Beibehalten" : "Passwort eintragen"} autoComplete="new-password" />
          </label>
          <label>
            <span>Absender-E-Mail</span>
            <Input type="email" value={smtpForm.from_email} onChange={(event) => updateSmtpField("from_email", event.target.value)} placeholder="noreply@aion.bidblitz.ae" />
          </label>
          <label>
            <span>Öffentliche AION URL</span>
            <Input value={smtpForm.public_app_url} onChange={(event) => updateSmtpField("public_app_url", event.target.value)} placeholder="https://aion.bidblitz.ae" />
          </label>

          <div className="aion-button-row">
            <Button type="submit" disabled={smtpBusy || !smtpForm.host.trim() || !smtpForm.from_email.trim()}><Mail size={16} /> SMTP speichern</Button>
            <Button type="button" variant="outline" disabled={smtpBusy || !smtpStatus?.ready} onClick={testSmtp}><PlugZap size={16} /> Verbindung prüfen</Button>
            <Button type="button" variant="outline" disabled={smtpBusy || (!smtpStatus?.configured && !smtpStatus?.password_configured)} onClick={removeSmtp}><Trash2 size={16} /> SMTP entfernen</Button>
          </div>
        </form>

        {smtpMissing.length ? (
          <div className="aion-note"><Server size={16} /> Noch offen: {smtpMissing.join(", ")}</div>
        ) : null}
        {smtpStatus?.ready ? (
          <div className="aion-note"><Globe2 size={16} /> Registrierung und E-Mail-Verifizierung sind serverseitig bereit.</div>
        ) : null}
        {smtpNotice ? <div className="aion-note"><ShieldCheck size={16} /> {smtpNotice}</div> : null}
        {smtpError ? <div className="aion-error-box">{smtpError}</div> : null}
        <div className="aion-note">Der Verbindungstest authentifiziert nur beim SMTP-Server; er verschickt keine E-Mail.</div>
      </AionSectionCard>
    </div>
  );
};
